import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Sparkles, TrendingUp, TrendingDown, Minus, AlertTriangle,
  Trophy, ChevronRight, Brain, Clock, Trash2,
} from "lucide-react";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell,
} from "recharts";

const TREND_META = {
  up:   { icon: TrendingUp,   cls: "text-emerald-600 bg-emerald-50",   label: "Up" },
  down: { icon: TrendingDown, cls: "text-rose-600 bg-rose-50",         label: "Down" },
  flat: { icon: Minus,        cls: "text-slate-500 bg-slate-100",      label: "Flat" },
};
const SEV_META = {
  high: "bg-rose-50 border-rose-200 text-rose-800",
  med:  "bg-amber-50 border-amber-200 text-amber-800",
  low:  "bg-sky-50 border-sky-200 text-sky-800",
};

function fmtMoney(n) { try { return "₱" + Number(n || 0).toLocaleString(undefined, {maximumFractionDigits: 0}); } catch { return n; } }
function fmtDate(iso) { try { return new Date(iso).toLocaleDateString(undefined, {month:"short", day:"numeric", year:"numeric"}); } catch { return iso; } }

export default function InsightsPage() {
  const { user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [period, setPeriod] = useState("weekly");
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const isSuper = user?.role === "super_admin" || user?.role === "sub_super_admin";
  const canRunNow = isSuper;

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/ai-analyses/schools");
        const list = r.data?.items || [];
        setSchools(list);
        if (list.length) setSchoolId(list[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    setSelected(null);
    (async () => {
      try {
        const r = await api.get("/ai-analyses", { params: { school_id: schoolId, period } });
        const items = r.data?.items || [];
        setHistory(items);
        if (items.length) {
          // Fetch the latest with full metrics
          const r2 = await api.get(`/ai-analyses/${items[0].id}`);
          setSelected(r2.data);
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [schoolId, period]);

  const pickHistory = async (id) => {
    setLoading(true);
    try {
      const r = await api.get(`/ai-analyses/${id}`);
      setSelected(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const runNow = async () => {
    if (!schoolId) return;
    if (!window.confirm("Generate a new AI analysis now? This can take up to a minute.")) return;
    setBusy(true);
    try {
      // GPT-5.2 + Mongo aggregations can take 30-60s — bump the per-call
      // timeout to 2 minutes so we don't bail before the LLM responds.
      const r = await api.post(
        "/admin/ai-analyses/run-now",
        { school_id: schoolId, period },
        { timeout: 120000 },
      );
      toast.success(r.data?.fallback ? "Analysis saved (LLM fallback)" : "Fresh analysis generated");
      // Reload list
      const lr = await api.get("/ai-analyses", { params: { school_id: schoolId, period } });
      setHistory(lr.data?.items || []);
      if (r.data?.id) pickHistory(r.data.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Super-admin-only: delete a single generated weekly/monthly insight. We
  // refresh the history sidebar locally instead of re-fetching for snappier
  // feedback, and surface the next-newest insight if the deleted one was
  // currently open.
  const deleteAnalysis = async (id, generatedAt) => {
    if (!id) return;
    const label = generatedAt ? fmtDate(generatedAt) : id;
    if (!window.confirm(`Delete the ${period} insight from ${label}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/ai-analyses/${id}`);
      toast.success("Insight deleted");
      const next = history.filter((h) => h.id !== id);
      setHistory(next);
      if (selected?.id === id) {
        if (next.length) {
          await pickHistory(next[0].id);
        } else {
          setSelected(null);
        }
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="space-y-5" data-testid="insights-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs text-slate-400 uppercase font-heading flex items-center gap-1.5"><Brain className="w-3.5 h-3.5" /> AI Powered</p>
            <h1 className="font-heading text-3xl font-bold">School Insights</h1>
            <p className="text-sm text-brand-inkMuted mt-1">
              GPT-5.2 analyses your attendance, finances, KPI and enrollment data every <strong>Saturday 8 PM</strong> and on the <strong>1st of each month</strong>.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={schoolId} onValueChange={setSchoolId}>
              <SelectTrigger className="w-56" data-testid="insights-school"><SelectValue placeholder="Select school" /></SelectTrigger>
              <SelectContent>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32" data-testid="insights-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
            {canRunNow && (
              <Button onClick={runNow} disabled={busy || !schoolId} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="insights-run-now">
                <Sparkles className="w-4 h-4 mr-1.5" /> {busy ? "Generating…" : "Run now"}
              </Button>
            )}
          </div>
        </div>

        {loading ? <p className="text-center text-slate-500 py-10">Loading…</p> :
         !selected ? (
          <Card className="p-10 rounded-3xl text-center" data-testid="insights-empty">
            <Brain className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="font-heading font-bold">No analysis yet</p>
            <p className="text-sm text-slate-500 mt-1">The first scheduled run is the upcoming Saturday at 8 PM.</p>
          </Card>
         ) : (
          <div className="grid lg:grid-cols-4 gap-4">
            {/* History sidebar */}
            <Card className="p-4 rounded-3xl border-[#2563EB]/10 lg:col-span-1 max-h-[80vh] overflow-y-auto" data-testid="insights-history">
              <div className="text-xs uppercase font-bold text-slate-500 mb-2 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> History</div>
              <div className="space-y-1">
                {history.map((h) => {
                  const isActive = selected?.id === h.id;
                  return (
                    <div
                      key={h.id}
                      className={`group flex items-center gap-1 rounded-2xl transition ${isActive ? "bg-[#EFF6FF] text-[#1D4ED8] font-semibold" : "hover:bg-slate-50"}`}
                      data-testid={`insights-history-row-${h.id}`}
                    >
                      <button
                        onClick={() => pickHistory(h.id)}
                        className="flex-1 text-left text-sm pl-3 pr-1 py-2 flex items-center justify-between gap-2"
                      >
                        <span>{fmtDate(h.generated_at)}</span>
                        {isActive && <ChevronRight className="w-4 h-4" />}
                      </button>
                      {isSuper && (
                        <button
                          onClick={() => deleteAnalysis(h.id, h.generated_at)}
                          disabled={busy}
                          className="p-1.5 mr-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                          aria-label="Delete insight"
                          title="Delete this insight"
                          data-testid={`insights-delete-${h.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>

            <div className="lg:col-span-3 space-y-4">
              <InsightContent data={selected} />
            </div>
          </div>
         )}
      </div>
    </>
  );
}

function InsightContent({ data }) {
  const ai = data.ai_output || {};
  const m = data.metrics || {};

  const headerRange = `${fmtDate(m.window?.start)} → ${fmtDate(m.window?.end)}`;
  return (
    <>
      <Card className="p-5 rounded-3xl border-[#2563EB]/10" data-testid="insights-summary">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-[#1D4ED8]" />
          <h2 className="font-heading text-xl font-bold">Executive summary</h2>
          <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full ml-auto">{data.period}</span>
        </div>
        <p className="text-xs text-slate-500 mb-2">{headerRange}</p>
        <ul className="space-y-1.5 text-sm">
          {(ai.executive_summary || []).map((b, i) => (
            <li key={i} className="flex items-start gap-2"><span className="text-[#1D4ED8] mt-0.5">•</span><span>{b}</span></li>
          ))}
        </ul>
      </Card>

      {Object.keys(ai.per_metric || {}).length > 0 && (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="insights-metrics">
          {Object.entries(ai.per_metric).map(([k, v]) => <MetricCard key={k} name={k} val={v} />)}
        </div>
      )}

      {(ai.branch_ranking && Object.keys(ai.branch_ranking).length > 0) && (
        <BranchRankings ranking={ai.branch_ranking} />
      )}

      {(ai.risks || []).length > 0 && (
        <Card className="p-5 rounded-3xl border-[#2563EB]/10" data-testid="insights-risks">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h2 className="font-heading text-xl font-bold">Risks & Recommendations</h2>
          </div>
          <div className="space-y-2">
            {ai.risks.map((r, i) => (
              <div key={i} className={`p-3 rounded-2xl border ${SEV_META[r.severity] || SEV_META.low}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-xs uppercase font-bold tracking-wider">{r.severity} • {r.area}</span>
                </div>
                <p className="text-sm font-medium mt-1">{r.message}</p>
                {r.recommended_action && <p className="text-xs italic mt-1 opacity-80">→ {r.recommended_action}</p>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {(ai.forecast || []).length > 0 && (
        <Card className="p-5 rounded-3xl border-[#2563EB]/10" data-testid="insights-forecast">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-violet-500" />
            <h2 className="font-heading text-xl font-bold">Forecast</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {ai.forecast.map((f, i) => (
              <div key={i} className="p-3 rounded-2xl bg-violet-50/60 border border-violet-200">
                <div className="text-xs uppercase font-bold text-violet-700">{f.metric} • {f.horizon}</div>
                <p className="text-sm mt-1">{f.projection}</p>
                <span className="text-[11px] mt-1 inline-block bg-white px-2 py-0.5 rounded-full">Confidence: {f.confidence}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {ai.fallback && (
        <Card className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="insights-fallback">
          The latest scheduled run could not reach the AI model. Insights will refresh on the next cycle.
        </Card>
      )}
    </>
  );
}

function MetricCard({ name, val }) {
  const t = TREND_META[val.trend] || TREND_META.flat;
  const Icon = t.icon;
  const pretty = name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const sign = val.delta_pct > 0 ? "+" : "";
  return (
    <Card className="p-4 rounded-3xl border-[#2563EB]/10">
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase font-bold text-slate-500 tracking-wider">{pretty}</h3>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${t.cls}`}>
          <Icon className="w-3 h-3" />{sign}{(val.delta_pct || 0).toFixed(0)}%
        </span>
      </div>
      <p className="text-sm text-slate-700 mt-2">{val.narrative}</p>
    </Card>
  );
}

function BranchRankings({ ranking }) {
  const labels = {
    student_absences: "Student absences (lower = better)",
    teacher_attendance: "Teacher attendance",
    enrollments: "New enrollments",
    income: "Income",
    net_profit: "Net profit",
  };
  const items = Object.entries(ranking).filter(([, list]) => Array.isArray(list) && list.length > 0);
  if (items.length === 0) return null;
  return (
    <Card className="p-5 rounded-3xl border-[#2563EB]/10" data-testid="insights-rankings">
      <div className="flex items-center gap-2 mb-3"><Trophy className="w-5 h-5 text-amber-500" /><h2 className="font-heading text-xl font-bold">Branch comparison</h2></div>
      <div className="grid lg:grid-cols-2 gap-5">
        {items.map(([metric, list]) => {
          const isMoney = metric === "income" || metric === "net_profit";
          const sorted = [...list].sort((a, b) => (a.rank || 99) - (b.rank || 99));
          return (
            <div key={metric}>
              <p className="text-xs uppercase font-bold text-slate-500 tracking-wider mb-1">{labels[metric] || metric}</p>
              <div style={{ width: "100%", height: 180 }}>
                <ResponsiveContainer>
                  <BarChart data={sorted}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="branch_name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => isMoney ? "₱" + Math.round(v/1000) + "k" : v} />
                    <Tooltip formatter={(v) => isMoney ? fmtMoney(v) : v} />
                    <Bar dataKey="score">
                      {sorted.map((row, i) => <Cell key={row.branch_id || row.branch_name || `c-${i}`} fill={i === 0 ? "#10b981" : "#94a3b8"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Top: <span className="font-semibold text-emerald-700">{sorted[0]?.branch_name}</span>
                {sorted[0]?.score != null && <> ({isMoney ? fmtMoney(sorted[0].score) : sorted[0].score})</>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
